'use client';

import { createClient } from '@/lib/supabase/client';

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /type.*does not exist/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

// ── Favourites ──────────────────────────────────────────────

export interface FavouritePackage {
  id: string;
  packageId: string;
  packageName: string;
  createdAt: string;
}

export async function getFavouritePackages(userId: string): Promise<FavouritePackage[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('favourite_packages')
      .select('id, package_id, package_name, created_at')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (
      data?.map((r) => ({
        id: r.id,
        packageId: r.package_id,
        packageName: r.package_name,
        createdAt: r.created_at,
      })) || []
    );
  } catch {
    return [];
  }
}

export async function getFavouritePackageIds(userId: string): Promise<string[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('favourite_packages')
      .select('package_id')
      .eq('user_id', userId);
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data?.map((r) => r.package_id) || [];
  } catch {
    return [];
  }
}

export async function addFavouritePackage(
  userId: string,
  packageId: string,
  packageName: string
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('favourite_packages')
    .upsert({ user_id: userId, package_id: packageId, package_name: packageName }, { onConflict: 'user_id,package_id' });
  if (error && isSchemaError(error)) throw error;
}

export async function removeFavouritePackage(userId: string, packageId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('favourite_packages')
    .delete()
    .eq('user_id', userId)
    .eq('package_id', packageId);
  if (error && isSchemaError(error)) throw error;
}

// ── Price Alerts ─────────────────────────────────────────────

export interface PriceAlert {
  id: string;
  packageId: string;
  packageName: string;
  currentPrice: number;
  contact: string;
  contactType: string;
  isActive: boolean;
  createdAt: string;
}

export async function getPriceAlerts(userId: string): Promise<PriceAlert[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('price_alerts')
      .select('id, package_id, package_name, current_price, contact, contact_type, is_active, created_at')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (
      data?.map((r) => ({
        id: r.id,
        packageId: r.package_id,
        packageName: r.package_name,
        currentPrice: r.current_price,
        contact: r.contact,
        contactType: r.contact_type,
        isActive: r.is_active,
        createdAt: r.created_at,
      })) || []
    );
  } catch {
    return [];
  }
}

export async function getPriceAlertPackageIds(userId: string): Promise<string[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('price_alerts')
      .select('package_id')
      .eq('user_id', userId)
      .eq('is_active', true);
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return data?.map((r) => r.package_id) || [];
  } catch {
    return [];
  }
}

export async function upsertPriceAlert(
  userId: string,
  packageId: string,
  packageName: string,
  currentPrice: number,
  contact: string,
  contactType: string
): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('price_alerts')
    .upsert(
      {
        user_id: userId,
        package_id: packageId,
        package_name: packageName,
        current_price: currentPrice,
        contact,
        contact_type: contactType,
        is_active: true,
      },
      { onConflict: 'user_id,package_id' }
    );
  if (error && isSchemaError(error)) throw error;
}

export async function removePriceAlert(userId: string, packageId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('price_alerts')
    .delete()
    .eq('user_id', userId)
    .eq('package_id', packageId);
  if (error && isSchemaError(error)) throw error;
}

export async function deactivatePriceAlert(userId: string, packageId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('price_alerts')
    .update({ is_active: false })
    .eq('user_id', userId)
    .eq('package_id', packageId);
  if (error && isSchemaError(error)) throw error;
}

// ── Booking History ──────────────────────────────────────────

export interface BookingRecord {
  appointmentId: string;
  service: string;
  bookingDate: string;
  bookingTime: string;
  location: string;
  patientName: string;
  totalAmount: string;
  isHomeCollection: boolean;
  status: string;
  cancelledAt?: string | null;
  cancelReason?: string | null;
}

export async function saveBooking(userId: string, booking: BookingRecord): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase.from('booking_history').insert({
    user_id: userId,
    appointment_id: booking.appointmentId,
    service: booking.service,
    booking_date: booking.bookingDate,
    booking_time: booking.bookingTime,
    location: booking.location,
    patient_name: booking.patientName,
    total_amount: booking.totalAmount,
    is_home_collection: booking.isHomeCollection,
    status: booking.status,
  });
  if (error && isSchemaError(error)) throw error;
}

export async function getBookingHistory(userId: string): Promise<BookingRecord[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('booking_history')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (
      data?.map((r) => ({
        appointmentId: r.appointment_id,
        service: r.service,
        bookingDate: r.booking_date,
        bookingTime: r.booking_time,
        location: r.location,
        patientName: r.patient_name,
        totalAmount: r.total_amount,
        isHomeCollection: r.is_home_collection,
        status: r.status,
        cancelledAt: r.cancelled_at || null,
        cancelReason: r.cancel_reason || null,
      })) || []
    );
  } catch {
    return [];
  }
}

export async function cancelBooking(userId: string, appointmentId: string, reason: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('booking_history')
    .update({
      status: 'cancelled',
      cancelled_at: new Date().toISOString(),
      cancel_reason: reason,
    })
    .eq('user_id', userId)
    .eq('appointment_id', appointmentId);
  if (error && isSchemaError(error)) throw error;
}

export async function requestReschedule(userId: string, appointmentId: string): Promise<void> {
  const supabase = createClient();
  const { error } = await supabase
    .from('booking_history')
    .update({
      reschedule_requested_at: new Date().toISOString(),
    })
    .eq('user_id', userId)
    .eq('appointment_id', appointmentId);
  if (error && isSchemaError(error)) throw error;
}

// ── Payment Receipts ─────────────────────────────────────────

export interface PaymentReceipt {
  id: string;
  appointmentId: string;
  receiptNumber: string;
  service: string;
  amount: number;
  paymentMethod: string;
  paymentStatus: string;
  paymentDate: string;
  transactionId: string | null;
  notes: string | null;
  createdAt: string;
}

export async function getPaymentReceipts(userId: string): Promise<PaymentReceipt[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('payment_receipts')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (
      data?.map((r) => ({
        id: r.id,
        appointmentId: r.appointment_id,
        receiptNumber: r.receipt_number,
        service: r.service,
        amount: r.amount,
        paymentMethod: r.payment_method,
        paymentStatus: r.payment_status,
        paymentDate: r.payment_date,
        transactionId: r.transaction_id,
        notes: r.notes,
        createdAt: r.created_at,
      })) || []
    );
  } catch {
    return [];
  }
}

// ── Test Results ─────────────────────────────────────────────

export interface TestResultParameter {
  name: string;
  value: string;
  range: string;
  status: string;
}

export interface TestResult {
  id: string;
  appointmentId: string;
  testName: string;
  category: string;
  resultStatus: string;
  reportUrl: string | null;
  reportDate: string | null;
  notes: string | null;
  parameters: TestResultParameter[];
  createdAt: string;
}

export async function getTestResults(userId: string): Promise<TestResult[]> {
  const supabase = createClient();
  try {
    const { data, error } = await supabase
      .from('test_results')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false });
    if (error) {
      if (isSchemaError(error)) throw error;
      return [];
    }
    return (
      data?.map((r) => ({
        id: r.id,
        appointmentId: r.appointment_id,
        testName: r.test_name,
        category: r.category,
        resultStatus: r.result_status,
        reportUrl: r.report_url,
        reportDate: r.report_date,
        notes: r.notes,
        parameters: Array.isArray(r.parameters) ? r.parameters : [],
        createdAt: r.created_at,
      })) || []
    );
  } catch {
    return [];
  }
}
